import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "@/lib/content";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/context/LangContext";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full border border-white/15 p-1" data-testid="lang-switch">
      {["it", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-toggle-${l}`} aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${lang === l ? "bg-white text-ink" : "text-zinc-300 hover:text-white"}`}>
          {l}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${scrolled ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl" : "border-b border-transparent"}`}
      >
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 md:h-[72px] md:px-10">
          <Logo onClick={() => go("home")} />
          <nav className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((id) => (
              <button key={id} onClick={() => go(id)} data-testid={`nav-link-${id}`}
                className="group relative font-mono text-xs uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:text-white">
                {t.nav[id]}
                <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-volt transition-transform duration-300 group-hover:scale-x-100" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LangSwitch />
            <button onClick={() => go("contatti")} data-testid="nav-contact-btn"
              className="group hidden items-center gap-2 rounded-full bg-trident px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-[1.04] sm:flex">
              {t.nav.contatti} <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button onClick={() => setOpen((o) => !o)} data-testid="nav-menu-toggle" aria-label="Menu" aria-expanded={open}
              className="group relative grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white transition-colors duration-300 hover:border-white/40 lg:hidden">
              <span className="relative block h-3.5 w-5">
                {[0, 1, 2].map((i) => (
                  <motion.span key={i} className="absolute left-0 block h-[2px] w-full origin-center rounded-full bg-white"
                    style={{ top: `${i * 6}px` }}
                    animate={open
                      ? (i === 1 ? { opacity: 0, scaleX: 0 } : { y: i === 0 ? 6 : -6, rotate: i === 0 ? 45 : -45 })
                      : { opacity: 1, scaleX: 1, y: 0, rotate: 0 }}
                    transition={{ duration: 0.45, ease: [0.76, 0, 0.24, 1], delay: i === 1 ? 0 : 0.05 }} />
                ))}
              </span>
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-40 flex flex-col justify-end bg-ink px-5 pb-12 pt-24 lg:hidden"
          >
            {[...NAV_LINKS, "contatti"].map((id, i) => (
              <motion.button key={id} onClick={() => go(id)} data-testid={`mobile-link-${id}`}
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05 }}
                className="flex items-baseline gap-4 border-b border-white/10 py-3 text-left font-display text-5xl font-black uppercase text-white">
                <span className="font-mono text-xs text-volt">0{i + 1}</span>{t.nav[id]}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
